import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";

import RadioButtons, { RadioButton } from "src/components/common/RadioButton";
import Button from "src/components/common/Button";
import { SettingsSelectors } from "src/redux/Settings/selectors";
import { AuthSelectors } from "src/redux/Auth/selectors";
import { SettingsActions } from "src/redux/Settings/actions";
import * as Validators from "src/utils/validators";

import styles from "./styles.module.scss";
import { moveNext, movePrev } from "src/utils/array";
import { PREFERRED_CRITERION } from "src/redux/Settings/constants";
import { ModalActions } from "src/redux/Modal/actions";

export const VD_T1 = { label: "T1", value: "T_1" };
export const VD_T2 = { label: "T2", value: "T_2" };
const VD_OPTIONS = [VD_T1, VD_T2];

export default (props) => {
    const dispatch = useDispatch();
    const isTraderRole = useSelector(AuthSelectors.IsTraderRole);

    const aoSettings = useSelector(SettingsSelectors.AOSettings);
    const listSettings = useSelector(SettingsSelectors.ListSettings);

    const [maxQuantity, setMaxQuantity] = useState(aoSettings.maxQuantity);
    const [maxMyRate, setMaxMyRate] = useState(aoSettings.maxMyRate);
    const [maxMyUtilization, setMaxMyUtilization] = useState(100);
    const [minValue, setMinValue] = useState(aoSettings.minValue ?? 0);
    const [defaultValueDate, setDefaultValueDate] = useState(
        aoSettings.defaultValueDate
    );
    const [preferredCriterion, setPreferredCriterion] = useState(
        aoSettings.preferredCriterion || PREFERRED_CRITERION.EQUAL
    );
    const [selectedIdx, setSelectedIdx] = useState(0);

    useEffect(() => {
        dispatch(
            SettingsActions.UpdateAOSettings({
                maxQuantity,
                maxMyRate,
                maxMyUtilization,
                defaultValueDate,
                preferredCriterion,
                minValue,
            })
        );
    }, [
        dispatch,
        maxQuantity,
        maxMyRate,
        maxMyUtilization,
        defaultValueDate,
        preferredCriterion,
        minValue,
    ]);

    const isAOSettingsValid =
        Validators.isMaxQtyValid(maxQuantity) &&
        Validators.isMaxRateValid(maxMyRate) &&
        Validators.isMaxUtilizationValid(maxMyUtilization) &&
        Validators.isMinValueValid(minValue);

    const move = (position) => {
        let result, newIdx;
        if (position === "up") {
            result = movePrev(listSettings, selectedIdx);
        } else {
            result = moveNext(listSettings, selectedIdx);
        }

        if (result) {
            dispatch(SettingsActions.SetListSettings(result));
            newIdx = position === "up" ? selectedIdx - 1 : selectedIdx + 1;
            setSelectedIdx(newIdx);
            document.getElementById(`listName-${newIdx}`).scrollIntoView();
        }
    };

    return (
        <div className={styles.autoOfferSettings}>
            <div className={styles.body}>
                <div className={styles.leftSection}>
                    <Section
                        title="Qty"
                        className={
                            Validators.isMaxQtyValid(maxQuantity)
                                ? null
                                : styles.error
                        }
                    >
                        <label>Max Auto Offer Qty</label>
                        <span className={styles.equal}>=</span>
                        <span>My Qty</span>
                        <span className={styles.sign}>x</span>
                        <input
                            data-testid="ao-qty"
                            value={maxQuantity}
                            onChange={(e) =>
                                !isNaN(e.target.value) &&
                                setMaxQuantity(e.target.value)
                            }
                        />
                        <span className={styles.sign}>%</span>
                    </Section>

                    <div className={styles.splitter} />

                    <Section
                        title="Rate"
                        className={
                            Validators.isMaxRateValid(maxMyRate)
                                ? null
                                : styles.error
                        }
                    >
                        <label>Max Auto Offer Rate</label>
                        <span className={styles.equal}>=</span>
                        <span>My Rate</span>
                        <span className={styles.sign}>{"<"}</span>
                        <input
                            data-testid="ao-rate"
                            value={maxMyRate}
                            onChange={(e) =>
                                !isNaN(e.target.value) &&
                                setMaxMyRate(e.target.value)
                            }
                        />
                        <span className={styles.sign}>%</span>
                    </Section>
                    <div className={styles.splitter} />
                    <div className={styles.hiddenSection}>
                        <div className={styles.splitter} />
                        <Section
                            title="Utilization"
                            className={
                                Validators.isMaxUtilizationValid(
                                    maxMyUtilization
                                )
                                    ? null
                                    : styles.error
                            }
                        >
                            <label>
                                Max Auto Offer <br />
                                Utilization{" "}
                            </label>
                            <span className={styles.equal}>=</span>
                            <span>
                                Tru
                                <br /> Utilization
                            </span>
                            <span className={styles.sign}>{"<"}</span>
                            <input
                                data-testid="ao-util"
                                value={maxMyUtilization}
                                onChange={(e) =>
                                    !isNaN(e.target.value) &&
                                    setMaxMyUtilization(e.target.value)
                                }
                            />
                            <span className={styles.sign}>%</span>
                        </Section>
                        <div className={styles.splitter} />
                    </div>

                    <Section
                        title="Min Value"
                        className={
                            Validators.isMinValueValid(minValue)
                                ? null
                                : styles.error
                        }
                    >
                        <label>Min Auto Offer Value</label>
                        <span className={styles.equal}>=</span>
                        <span>Value</span>
                        <span className={styles.sign}>{">"}</span>
                        <input
                            data-testid="ao-value"
                            value={minValue}
                            onChange={(e) =>
                                !isNaN(e.target.value) &&
                                setMinValue(e.target.value)
                            }
                        />
                        <span className={styles.sign}>USD</span>
                    </Section>
                </div>
                <div className={styles.rightSection}>
                    <div className={styles.defaultVD}>
                        <div className={styles.subTitle}>
                            Default Value Date
                        </div>
                        <div className={styles.radioGroup}>
                            <RadioButtons
                                options={VD_OPTIONS}
                                dataTestId="ao-vd"
                                value={defaultValueDate}
                                setter={setDefaultValueDate}
                            />
                        </div>
                    </div>

                    <div className={styles.splitter} />

                    <div>
                        <div className={styles.title}>Preferred Criterion</div>
                        <div
                            className={[
                                styles.detail,
                                styles.preferredCriterion,
                            ].join(" ")}
                        >
                            <div
                                className={styles.radio}
                                data-testid="radio-equal-select"
                            >
                                <RadioButton
                                    key="equal"
                                    labelPosition="left"
                                    value={preferredCriterion}
                                    selected={
                                        preferredCriterion ===
                                        PREFERRED_CRITERION.EQUAL
                                    }
                                    onChange={(e) =>
                                        setPreferredCriterion(
                                            PREFERRED_CRITERION.EQUAL
                                        )
                                    }
                                    dataTestId="radio-equal"
                                >
                                    <span>Equal %</span>
                                </RadioButton>
                            </div>
                            <div
                                className={styles.radio}
                                data-testid="radio-order-select"
                            >
                                <RadioButton
                                    key="listOrder"
                                    labelPosition="listOrder"
                                    value={preferredCriterion}
                                    disabled={
                                        !listSettings ||
                                        listSettings.length === 0
                                    }
                                    selected={
                                        preferredCriterion ===
                                        PREFERRED_CRITERION.LIST_ORDER
                                    }
                                    onChange={(e) =>
                                        setPreferredCriterion(
                                            PREFERRED_CRITERION.LIST_ORDER
                                        )
                                    }
                                    dataTestId="radio-list-order"
                                >
                                    <span>List Order</span>
                                </RadioButton>
                            </div>
                        </div>
                    </div>
                    <div
                        className={
                            preferredCriterion === PREFERRED_CRITERION.EQUAL
                                ? styles.disableWrapper
                                : ""
                        }
                    >
                        <div className={styles.title} data-testid="list-order">
                            List Order
                            <div
                                className={[
                                    styles.arrDown,
                                    selectedIdx === listSettings.length - 1
                                        ? styles.disabled
                                        : "",
                                ].join(" ")}
                                data-testid="list-order-down"
                                onClick={() => move("down")}
                            />
                            <div
                                className={[
                                    styles.arrUp,
                                    selectedIdx === 0 ? styles.disabled : "",
                                ].join(" ")}
                                data-testid="list-order-up"
                                onClick={() => move("up")}
                            />
                        </div>
                        <div
                            className={[styles.detail, styles.listOrder].join(
                                " "
                            )}
                        >
                            {listSettings &&
                                listSettings.map((list, index) => {
                                    return (
                                        <div
                                            id={`listName-${index}`}
                                            data-testid={`list-name-${list.name}`}
                                            onClick={() =>
                                                setSelectedIdx(index)
                                            }
                                            className={[
                                                styles.listName,
                                                selectedIdx === index
                                                    ? styles.selected
                                                    : "",
                                            ].join(" ")}
                                            key={`list-${index}`}
                                        >
                                            <div className={styles.index}>
                                                {index + 1}
                                            </div>
                                            <div className={styles.name}>
                                                {list.name}
                                            </div>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.footer}>
                <Button
                    value="Save"
                    onClick={() =>
                        dispatch(
                            SettingsActions.SaveCPTYSettings(
                                preferredCriterion ===
                                    PREFERRED_CRITERION.LIST_ORDER
                            )
                        )
                    }
                    dataTestId="save-ao-settings"
                    disabled={!isTraderRole || !isAOSettingsValid}
                />

                <Button
                    onClick={() => dispatch(ModalActions.Close())}
                    color="reversedWhite"
                    dataTestId="cancel-list-settings"
                    value="Cancel"
                />
            </div>
        </div>
    );
};

const Section = ({ title, className, children }) => (
    <div className={[styles.section].concat(className || []).join(" ")}>
        <div className={styles.title}>{title}</div>
        <div className={styles.input}>{children}</div>
    </div>
);
